import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import classes from "./ScholarshipList.module.scss";
import FilterCard from "./FilterCard/FilterCard";
import CustomImage from "../../../CustomImage";
import ScholarshipCard from "./ScholarshipCard/ScholarshipCard";
import Pagination from "../../../UI/Pagination/Pagination";
import Spinner from "../../../UI/Spinner/Spinner";
import LeadGenForms from "../../../EnquiryModal/LeadGenForms";
import useIsMobile from "../../../../hooks/useIsMobile";
import { isFrappeOrJotFormClass } from "../../../../utils/helpers";

const CARDS_PER_PAGE = 10;

const ScholarshipList = ({ scholarshipCountryData, scholarshipsPrograms, searchScholarship }: any) => {
  const isTab = useIsMobile(1024);
  const isMobile = useIsMobile(600);
  const [currentPage, setCurrentPage] = useState(1);
  const [scholarships, setScholarships] = useState<any[]>([]);
  const [searchedUni, setSearchedUni] = useState("");
  const [loading, setLoading] = useState(false);
  const [maxCardHeight, setMaxCardHeight] = useState<number | undefined>(undefined);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const totalPages = Math.ceil(scholarships.length / CARDS_PER_PAGE);
  const paginatedScholarships = scholarships.slice((currentPage - 1) * CARDS_PER_PAGE, currentPage * CARDS_PER_PAGE);

  useLayoutEffect(() => {
    setMaxCardHeight(undefined);
  }, [currentPage, scholarships, isTab, isMobile]);

  useLayoutEffect(() => {
    if (maxCardHeight !== undefined) return;
    const heights = cardRefs.current.map((el) => el?.offsetHeight ?? 0);
    if (heights.length) setMaxCardHeight(Math.max(...heights));
  }, [maxCardHeight, currentPage, scholarships]);

  useEffect(() => setCurrentPage(1), [scholarships]);

  useEffect(() => {
    setScholarships(scholarshipsPrograms?.popularCountryPrograms ?? []);
  }, [scholarshipsPrograms]);

  const adClickHandler = (link: string) => () => {
    window.open(link, "_blank");
  };

  const adPanel = scholarshipCountryData?.adPanel;

  const getAdProps = (baseClass: string) => ({
    className: [
      baseClass,
      adPanel?.link || adPanel?.formId ? classes["is-clickable"] : "",
      !adPanel?.link && adPanel?.formId ? isFrappeOrJotFormClass(adPanel?.formId) : "",
    ]
      .filter(Boolean)
      .join(" "),
    onClick: adPanel?.link ? adClickHandler(adPanel.link) : undefined,
  });

  return (
    <section className={classes.section}>
      <div className={classes.content}>
        <h2 className={classes.heading}>{searchScholarship?.title ?? ""}</h2>

        <FilterCard setScholarships={setScholarships} setSearchedUni={setSearchedUni} setLoading={setLoading} />

        <div className={classes["results-area"]}>
          <div className={classes["results-grid"]}>
            {loading && (
              <div className={classes.loadingContainer}>
                <Spinner />
              </div>
            )}

            {!loading && (
              <div className={classes["results-main"]}>
                {isMobile && scholarshipCountryData?.adPanel?.img?.url && (
                  <div {...getAdProps(classes["sidebar-ad-mobile"])}>
                    <CustomImage src={scholarshipCountryData?.adPanel?.img?.url ?? ""} layout="fill" objectFit="fill" alt="ad-panel-img" />
                  </div>
                )}

                {isTab && !isMobile && scholarshipCountryData?.adPanel?.img?.url && (
                  <div {...getAdProps(classes["sidebar-ad-tab"])}>
                    <CustomImage src={scholarshipCountryData?.adPanel?.tabImg?.url ?? ""} layout="fill" objectFit="fill" alt="ad-panel-img" />
                  </div>
                )}

                {searchedUni?.length > 0 && (
                  <p>
                    {scholarships.length} scholarships found for
                    <strong> "{searchedUni}"</strong>
                  </p>
                )}
                <div className={classes["cards-list"]}>
                  {paginatedScholarships.length > 0 &&
                    paginatedScholarships.map((scholarship: any, index: number) => (
                      <ScholarshipCard
                        key={scholarship?.id ?? index}
                        cardData={scholarship}
                        minHeight={maxCardHeight}
                        ref={(el: HTMLDivElement | null) => (cardRefs.current[index] = el)}
                      />
                    ))}
                </div>

                {searchedUni.length === 0 && scholarships.length === 0 && <p className={classes["empty-list-msg"]}>No scholarships found !</p>}

                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
              </div>
            )}
            {!isTab && (
              <div className={classes.sidebar}>
                <div className={classes["sidebar-form"]}>
                  <div className={classes.jotFormHeader}>
                    <h3 className={classes["form-title"]}>Want to Study Abroad?</h3>
                    <p>Fill in your details and our experts will guide you to the best scholarship opportunities.</p>
                  </div>
                  <LeadGenForms
                    mode="inline"
                    url={process.env.NEXT_PUBLIC_CRM_CF_URL + "/" + scholarshipCountryData?.sidebarFormId}
                    wrapperClass={classes.inLineFormUniversity}
                    loader="shimmer"
                  />
                </div>
                {adPanel?.img?.url && (
                  <div {...getAdProps(classes["sidebar-ad-desktop"])}>
                    <CustomImage src={adPanel?.img?.url ?? ""} layout="fill" objectFit="cover" alt="ad-panel-img" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ScholarshipList;
